import axios, { AxiosError } from 'axios';
import * as React from 'react';
import { requestUpdateList } from '../asyncHelpers/listsService';
import { RequestStatus } from '../sic-lib/enums/RequestStatus';
import { UpdateListRequest } from '../sic-lib/types/Lists';
import { useAppDispatch } from '../store/hooks';
import { createToast } from '../store/notify';
import { createErrorToast } from '../utils/notifyHelpers';

/**
 * useAsyncModifyListView
 *
 * @description
 * React hook providing request state management and cancellation for the
 * requestUpdateList HTTP request
 *
 * @hidden
 * TIP: this hook is used in situations where a list's viewId and that view's properties
 * have been changed. In this use case both the list and the view will have to be updated
 * on the server.
 */
export default function useAsyncModifyListView() {
    const dispatch = useAppDispatch();
    const request_token = axios.CancelToken.source();

    React.useEffect(() => {
        return () => {
            request_token.cancel();
        };
    }, []);

    const [requestStatus, setRequestStatus] = React.useState<RequestStatus>(RequestStatus.none);

    const asyncModifyListView = async (list: UpdateListRequest): Promise<boolean | undefined> => {
        try {
            setRequestStatus(RequestStatus.active);

            await requestUpdateList(list, request_token.token);

            setRequestStatus(RequestStatus.success);

            return true;
        } catch (e: any) {
            if (!axios.isCancel(e)) {
                if (axios.isAxiosError(e)) {
                    const error = e as AxiosError;
                    if (e.response?.status == 400) {
                        /* Notify user of failed http request */
                        const toastData = createErrorToast(e.response.data);

                        dispatch(createToast({ toastData }));
                    }
                }
                /* Update state */
                setRequestStatus(RequestStatus.error);
            }
        }
    };

    return {
        requestStatus,
        asyncModifyListView,
    };
}
